import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import {
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Columns3,
  Download,
  GitCompareArrows,
  Loader2,
  Search,
  X,
} from 'lucide-react'
import clsx from 'clsx'
import { dbSearchService, downloadBlob, type ComparePeriodArgs } from '@/services/dbSearch'
import { ColumnPicker } from './ColumnPicker'
import { ComparePanel } from './ComparePanel'
import { DealDrawer } from './DealDrawer'
import { FilterRail } from './FilterRail'
import { ResultTable } from './ResultTable'
import { SavedSearchPanel } from './SavedSearchPanel'
import { SummaryStrip } from './SummaryStrip'
import { count } from './format'
import { openDealFile } from './lifecycle'
import {
  EMPTY_FILTERS,
  activeFilterChips,
  type CompareResult,
  type DealRow,
  type DealStatusFilter,
  type SavedSearch,
  type SearchFilters,
} from './types'

const PAGE_SIZE = 100

/** Filters change as fast as someone types; the query should not. */
function useDebounced<T>(value: T, delay = 220): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

/**
 * Deal Locator tab — every deal since inception, in one query.
 *
 * All of the work happens server-side (see `app/api/v1/deal_search.py`): the
 * browser holds one page of rows and a set of aggregates, never the corpus. The
 * page's job is to keep the filter state coherent and to make what is being
 * filtered visible at all times.
 */
export function DealLocator() {
  const queryClient = useQueryClient()

  const [filters, setFilters] = useState<SearchFilters>({ ...EMPTY_FILTERS })
  const [columns, setColumns] = useState<string[] | null>(null)
  const [sortField, setSortField] = useState('created_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [groupBy, setGroupBy] = useState('stage')
  const [selected, setSelected] = useState<DealRow | null>(null)

  const [showColumns, setShowColumns] = useState(false)
  const [showSaved, setShowSaved] = useState(false)
  const [showCompare, setShowCompare] = useState(false)
  const [activeViewId, setActiveViewId] = useState<string | null>(null)
  const [exportNote, setExportNote] = useState<string | null>(null)
  const [compareResult, setCompareResult] = useState<CompareResult | null>(null)

  const debouncedFilters = useDebounced(filters)

  const schemaQuery = useQuery({
    queryKey: ['db-search', 'schema'],
    queryFn: dbSearchService.getSchema,
    staleTime: 10 * 60_000,
  })
  const facetsQuery = useQuery({
    queryKey: ['db-search', 'facets'],
    queryFn: dbSearchService.getFacets,
    staleTime: 60_000,
  })

  const schema = schemaQuery.data
  // Memoized: this array is a react-query key and a useMemo dependency, so a
  // fresh `[]` on every render would refetch the search on every render.
  const effectiveColumns = useMemo(
    () => columns ?? schema?.defaultColumns ?? [],
    [columns, schema?.defaultColumns],
  )

  // Any filter change invalidates the current page number — staying on page 7
  // of a result set that now has two pages would show an empty grid.
  useEffect(() => {
    setPage(1)
  }, [debouncedFilters])

  const resultQuery = useQuery({
    queryKey: [
      'db-search',
      'query',
      debouncedFilters,
      effectiveColumns,
      sortField,
      sortDir,
      page,
      groupBy,
    ],
    queryFn: ({ signal }) =>
      dbSearchService.query(
        {
          filters: debouncedFilters,
          columns: effectiveColumns,
          sortField,
          sortDir,
          page,
          pageSize: PAGE_SIZE,
          groupBy,
        },
        signal,
      ),
    enabled: effectiveColumns.length > 0,
    // Keeps the previous page on screen while the next one loads, so paging and
    // filtering never flash an empty grid.
    placeholderData: keepPreviousData,
  })

  const savedQuery = useQuery({
    queryKey: ['db-search', 'views'],
    queryFn: dbSearchService.listViews,
    staleTime: 30_000,
  })

  const result = resultQuery.data
  const rows = result?.rows ?? []

  const columnSpecs = useMemo(() => {
    if (!schema) return []
    const byKey = new Map(schema.columns.map((column) => [column.key, column]))
    return effectiveColumns.map((key) => byKey.get(key)).filter(Boolean) as NonNullable<
      ReturnType<typeof byKey.get>
    >[]
  }, [schema, effectiveColumns])

  const patch = useCallback((next: Partial<SearchFilters>) => {
    setFilters((prev) => ({ ...prev, ...next }))
    setActiveViewId(null)
  }, [])

  const presetLabel = useCallback(
    (key: string) => schema?.datePresets.find((preset) => preset.key === key)?.label ?? key,
    [schema],
  )

  const chips = useMemo(() => activeFilterChips(filters, presetLabel), [filters, presetLabel])

  const runSaved = useMutation({
    mutationFn: ({ view, datePreset }: { view: SavedSearch; datePreset: string | null }) =>
      dbSearchService
        .runView(view.id, datePreset ? { datePreset } : {}, )
        .then((payload) => ({ view, payload, datePreset })),
    onSuccess: ({ view, payload, datePreset }) => {
      // Adopt exactly what the server ran, so the visible filter state and the
      // numbers on screen describe the same query.
      const merged = {
        ...EMPTY_FILTERS,
        ...(view.filters as Partial<SearchFilters>),
        ...(datePreset ? { datePreset } : {}),
      }
      setFilters(merged)
      setColumns(payload.columns)
      setSortField(payload.sortField)
      setSortDir(payload.sortDir)
      setActiveViewId(view.id)
      setPage(1)
      setShowSaved(false)
      void queryClient.invalidateQueries({ queryKey: ['db-search', 'views'] })
    },
  })

  const saveView = useMutation({
    mutationFn: ({ name, description }: { name: string; description: string }) =>
      dbSearchService.createView({
        name,
        description,
        filters,
        columns: effectiveColumns,
        sortField,
        sortDir,
      }),
    onSuccess: (view) => {
      setActiveViewId(view.id)
      void queryClient.invalidateQueries({ queryKey: ['db-search', 'views'] })
    },
  })

  const deleteView = useMutation({
    mutationFn: (view: SavedSearch) => dbSearchService.deleteView(view.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['db-search', 'views'] }),
  })

  const exportMutation = useMutation({
    mutationFn: () =>
      dbSearchService.exportXlsx({
        filters,
        columns: effectiveColumns,
        sortField,
        sortDir,
        title: activeViewId
          ? (savedQuery.data?.find((view) => view.id === activeViewId)?.name ?? 'Deal Locator Export')
          : 'Deal Locator Export',
      }),
    onSuccess: (outcome) => {
      downloadBlob(outcome.blob, outcome.filename)
      setExportNote(
        outcome.truncated
          ? `Exported the first ${count(outcome.rows)} of ${count(outcome.matched)} matching deals — narrow the filters to get the rest.`
          : `Exported ${count(outcome.rows)} deals to ${outcome.filename}.`,
      )
    },
    onError: async (error: unknown) => {
      let detail = ''
      if (axios.isAxiosError(error)) {
        const data = error.response?.data
        try {
          const parsed = data instanceof Blob ? JSON.parse(await data.text()) : data
          detail = typeof parsed?.detail === 'string' ? parsed.detail : ''
        } catch {
          detail = ''
        }
      }
      setExportNote(
        detail ? `Export blocked — ${detail}` : 'Export failed — the file was not created.',
      )
    },
  })

  const compareMutation = useMutation({
    mutationFn: (args: { dateField: string; periods: ComparePeriodArgs[] }) =>
      dbSearchService.compare({ filters, periods: args.periods, dateField: args.dateField }),
    onSuccess: setCompareResult,
  })

  useEffect(() => {
    if (!exportNote) return
    const timer = setTimeout(() => setExportNote(null), 9000)
    return () => clearTimeout(timer)
  }, [exportNote])

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('desc')
    }
    setPage(1)
  }

  const searchRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const searching = resultQuery.isFetching
  const errorMessage = resultQuery.isError
    ? ((resultQuery.error as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail as string) || 'The search could not be completed.'
    : null

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-3">
      {/* Toolbar. The module header lives in the app shell — this is the query
          bar, not a second page title. */}
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search
            size={13}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-600"
          />
          <input
            ref={searchRef}
            value={filters.q}
            onChange={(event) => patch({ q: event.target.value })}
            placeholder="Search every deal — property, sponsor, borrower, broker, id, investor…"
            aria-label="Search all deals"
            className="w-full rounded-lg border border-amber-300/10 bg-[#0b0b0b] py-1.5 pl-8 pr-16 text-[12px] text-stone-100 placeholder:text-stone-600 focus:border-amber-300/30 focus:outline-none"
          />
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 font-mono text-[9px] text-stone-700">
            ⌘K
          </span>
        </div>

        <select
          value={filters.status}
          onChange={(event) => patch({ status: event.target.value as DealStatusFilter })}
          aria-label="Deal status"
          className="rounded-lg border border-amber-300/10 bg-[#0b0b0b] px-2 py-1.5 text-[11px] text-stone-200 focus:border-amber-300/30 focus:outline-none"
        >
          {(schema?.statuses ?? []).map((status) => (
            <option key={status.key} value={status.key}>
              {status.label}
            </option>
          ))}
        </select>

        <select
          value={filters.datePreset ?? ''}
          onChange={(event) =>
            patch({ datePreset: event.target.value || null, dateFrom: null, dateTo: null })
          }
          aria-label="Date period"
          className="rounded-lg border border-amber-300/10 bg-[#0b0b0b] px-2 py-1.5 text-[11px] text-stone-200 focus:border-amber-300/30 focus:outline-none"
        >
          <option value="">Any period</option>
          {(schema?.datePresets ?? []).map((preset) => (
            <option key={preset.key} value={preset.key}>
              {preset.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setShowCompare((prev) => !prev)}
          aria-pressed={showCompare}
          className={clsx(
            'flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[11px] transition',
            showCompare
              ? 'border-amber-300/30 bg-amber-400/[0.1] text-amber-100'
              : 'border-amber-300/10 bg-[#0b0b0b] text-stone-300 hover:border-amber-300/25',
          )}
        >
          <GitCompareArrows size={12} /> Compare
        </button>

        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setShowColumns((prev) => !prev)
              setShowSaved(false)
            }}
            aria-expanded={showColumns}
            className="flex items-center gap-1.5 rounded-lg border border-amber-300/10 bg-[#0b0b0b] px-2 py-1.5 text-[11px] text-stone-300 transition hover:border-amber-300/25"
          >
            <Columns3 size={12} /> Columns
            <span className="font-mono text-[9px] text-stone-600">{effectiveColumns.length}</span>
          </button>
          {showColumns && schema && (
            <ColumnPicker
              schema={schema}
              selected={effectiveColumns}
              onChange={setColumns}
              onClose={() => setShowColumns(false)}
            />
          )}
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setShowSaved((prev) => !prev)
              setShowColumns(false)
            }}
            aria-expanded={showSaved}
            className={clsx(
              'flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[11px] transition',
              activeViewId
                ? 'border-amber-300/30 bg-amber-400/[0.1] text-amber-100'
                : 'border-amber-300/10 bg-[#0b0b0b] text-stone-300 hover:border-amber-300/25',
            )}
          >
            <Bookmark size={12} /> Reports
          </button>
          {showSaved && (
            <SavedSearchPanel
              views={savedQuery.data ?? []}
              schema={schema}
              loading={savedQuery.isLoading}
              busyId={runSaved.isPending ? (runSaved.variables?.view.id ?? null) : null}
              activeId={activeViewId}
              canSave={effectiveColumns.length > 0}
              onRun={(view, datePreset) => runSaved.mutate({ view, datePreset })}
              onSave={async (name, description) => {
                await saveView.mutateAsync({ name, description })
              }}
              onDelete={(view) => deleteView.mutate(view)}
              onClose={() => setShowSaved(false)}
            />
          )}
        </div>

        <button
          type="button"
          onClick={() => exportMutation.mutate()}
          disabled={exportMutation.isPending || !result || result.total === 0}
          title={
            result?.total
              ? `Download all ${count(result.total)} matching deals as Excel`
              : 'Nothing to export'
          }
          className="flex items-center gap-1.5 rounded-lg border border-amber-300/25 bg-amber-400/[0.1] px-2.5 py-1.5 text-[11px] font-medium text-amber-100 transition hover:bg-amber-400/[0.16] disabled:opacity-40"
        >
          {exportMutation.isPending ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Download size={12} />
          )}
          Export{result?.total ? ` ${count(result.total)}` : ''}
        </button>
      </div>


      {(chips.length > 0 || exportNote) && (
        <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <button
              key={`${String(chip.key)}-${chip.label}`}
              type="button"
              onClick={() => patch(chip.clear)}
              className="flex items-center gap-1 rounded-full border border-amber-300/20 bg-amber-400/[0.07] px-2 py-0.5 text-[10px] text-amber-100 transition hover:bg-amber-400/[0.14]"
            >
              {chip.label}
              <X size={9} />
            </button>
          ))}
          {chips.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setFilters({ ...EMPTY_FILTERS })
                setActiveViewId(null)
              }}
              className="text-[10px] text-stone-500 underline-offset-2 transition hover:text-amber-200 hover:underline"
            >
              Clear all
            </button>
          )}
          {exportNote && (
            <span
              role="status"
              className="ml-auto rounded border border-amber-300/15 bg-black/40 px-2 py-0.5 text-[10px] text-stone-300"
            >
              {exportNote}
            </span>
          )}
        </div>
      )}

      {showCompare && (
        <ComparePanel
          dateField={filters.dateField}
          result={compareResult}
          loading={compareMutation.isPending}
          error={compareMutation.isError ? 'The comparison could not be completed.' : null}
          onRun={(args) => compareMutation.mutate(args)}
          onClose={() => setShowCompare(false)}
        />
      )}

      <div className="flex min-h-0 flex-1 gap-2">
        <FilterRail
          filters={filters}
          schema={schema}
          facets={facetsQuery.data}
          onPatch={patch}
          onReset={() => {
            setFilters({ ...EMPTY_FILTERS })
            setActiveViewId(null)
          }}
          activeCount={chips.length}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <SummaryStrip
            totals={result?.totals}
            groups={result?.groups}
            groupBy={groupBy}
            schema={schema}
            loading={searching}
            onGroupByChange={setGroupBy}
            activeBucketValues={bucketSelection(filters, groupBy)}
            onDrillDown={(bucket) => {
              if (!bucket.value) return
              const key = DRILL_KEYS[groupBy]
              if (key) {
                const current = (filters[key] as string[]) ?? []
                patch({
                  [key]: current.includes(bucket.value)
                    ? current.filter((v) => v !== bucket.value)
                    : [...current, bucket.value],
                } as Partial<SearchFilters>)
              } else if (groupBy === 'stage') {
                patch({ stageFrom: bucket.value, stageTo: bucket.value })
              } else if (groupBy === 'deal_status') {
                patch({ status: bucket.value === 'dead' ? 'dead' : 'active' })
              }
            }}
          />

          <ResultTable
            rows={rows}
            columns={columnSpecs}
            selectedId={selected?.id ?? null}
            sortField={sortField}
            sortDir={sortDir}
            sortableFields={schema?.sortFields ?? []}
            loading={searching}
            error={errorMessage}
            onSelect={(row) => setSelected((prev) => (prev?.id === row.id ? null : row))}
            onOpen={(row) => {
              setSelected(row)
              openDealFile(row.id)
            }}
            onSort={toggleSort}
          />

          <div className="flex flex-shrink-0 items-center justify-between gap-3 px-1 text-[10px] text-stone-500">
            <span>
              {result ? (
                <>
                  Showing {count((result.page - 1) * result.pageSize + (rows.length ? 1 : 0))}–
                  {count((result.page - 1) * result.pageSize + rows.length)} of{' '}
                  {count(result.total)}
                  {searching ? ' · updating…' : ` · ${result.elapsedMs.toFixed(0)} ms`}
                  {rows.length > 0 && (
                    <span className="ml-2 text-stone-600">Double-click a deal to open its file</span>
                  )}
                </>
              ) : (
                'Loading…'
              )}
            </span>
            <span className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={!result || result.page <= 1}
                aria-label="Previous page"
                className="rounded border border-amber-300/10 p-1 transition enabled:hover:border-amber-300/30 enabled:hover:text-amber-200 disabled:opacity-30"
              >
                <ChevronLeft size={12} />
              </button>
              <span className="font-mono">
                {result?.page ?? 1} / {Math.max(1, result?.pageCount ?? 1)}
              </span>
              <button
                type="button"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={!result || result.page >= result.pageCount}
                aria-label="Next page"
                className="rounded border border-amber-300/10 p-1 transition enabled:hover:border-amber-300/30 enabled:hover:text-amber-200 disabled:opacity-30"
              >
                <ChevronRight size={12} />
              </button>
            </span>
          </div>
        </div>

        {selected && (
          <DealDrawer
            row={selected}
            onClose={() => setSelected(null)}
            onStatusChanged={() => {
              setSelected(null)
              void queryClient.invalidateQueries({ queryKey: ['db-search'] })
            }}
          />
        )}
      </div>
    </div>
  )
}

/** Group dimension → the filter list a bucket click toggles. Dimensions absent
 *  here are handled explicitly at the call site (stage becomes a range, status
 *  becomes the status selector). */
const DRILL_KEYS: Record<string, keyof SearchFilters | undefined> = {
  asset_type: 'assetTypes',
  assignee: 'assignees',
  originator: 'originators',
  investor: 'investors',
  capital_program: 'capitalPrograms',
  sponsor: 'sponsors',
  location: 'locations',
  broker_name: 'brokers',
  priority: 'priorities',
}

function bucketSelection(filters: SearchFilters, groupBy: string): string[] {
  const key = DRILL_KEYS[groupBy]
  if (key) return (filters[key] as string[]) ?? []
  if (groupBy === 'stage' && filters.stageFrom && filters.stageFrom === filters.stageTo) {
    return [filters.stageFrom]
  }
  if (groupBy === 'deal_status' && filters.status !== 'all') return [filters.status]
  return []
}
